import React, { useEffect, useState, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';

import Loader from '../../../../elements/Loader';
import FormBoolean from '../../../../elements/formElements/FormBoolean';
import FormFileGroup from '../../../../elements/formElements/FormFileGroup';
import SubmitButton from '../../../../elements/formElements/SubmitButton';
import axiosCRM from '../../../../services/MyAxios';
import { API_CASINO_SETTINGS_LOGO, API_CASINO_SETTINGS_LOGO_UPDATE } from '../../Helpers/CrmApiUrls';
import { CASINO_SETTINGS } from '../../Helpers/CrmUrls';

interface Settings {
    // eslint-disable-next-line camelcase
    casino_logo: string | File;
    // eslint-disable-next-line camelcase
    use_custom_logo: string;
}

function LogoSetting() {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<Settings>({
        casino_logo: '',
        use_custom_logo: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = (e): void => {
        e.preventDefault();

        axiosCRM
            .post(API_CASINO_SETTINGS_LOGO_UPDATE, settings, {
                headers: {
                    Accept: 'application/json',
                    'content-type': 'multipart/form-data',
                },
            })
            .then((response) => {
                if (response.status === 204) {
                    swal({
                        title: 'Success',
                        text: 'Logo settings updated correctly',
                        icon: 'success',
                    }).then(() => {
                        history.push(CASINO_SETTINGS);
                    });
                }
            })
            .catch((error) => {
                if (!error.response) {
                    setErrors({ message: error.message });
                } else if (error.response.status !== 500) {
                    setErrors(error.response.data.errors);
                } else {
                    swal({
                        title: 'Error',
                        text: 'Server error',
                        icon: 'error',
                    });
                }
            });
    };

    const onSwitchSelectorChange = (value: string) => {
        setSettings({ ...settings, use_custom_logo: value });
    };

    const getCurrentSettings = (): void => {
        axiosCRM
            .get(API_CASINO_SETTINGS_LOGO)
            .then((response) => {
                setSettings(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'Server error',
                    icon: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getCurrentSettings();
    }, []);

    const getCasinoLogoValue = () => {
        // was created to avoid browser cache
        if (typeof settings.casino_logo === 'string' && settings.casino_logo.length > 0) {
            return `${settings.casino_logo}?v=${Date.now()}`;
        }

        return settings.casino_logo;
    };

    const getContent = (): JSX.Element => {
        return (
            <form className="form-flex" onSubmit={handleSubmit}>
                <FormFileGroup
                    label="Logo"
                    field="casino_logo"
                    value={
                        typeof settings.casino_logo === 'undefined' || typeof settings.casino_logo === 'string'
                            ? getCasinoLogoValue()
                            : URL.createObjectURL(settings.casino_logo)
                    }
                    error={errors.casino_logo ?? undefined}
                    handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSettings({
                            ...settings,
                            casino_logo: event.target.files[0],
                        });
                    }}
                    acceptExtensions=".png"
                    classes="logo-image"
                />
                <FormBoolean
                    label="Use different logo for casino"
                    handleChange={onSwitchSelectorChange}
                    currentValue={settings.use_custom_logo}
                    required
                    field="use_custom_logo"
                    error={errors.use_custom_logo ?? undefined}
                />
                <SubmitButton isLoading={isLoading} onClick={handleSubmit} label="Save" />
            </form>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}

export default LogoSetting;
